import { FC } from 'react'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as DiagonalArrowUp } from 'src/assets/svg/diagonalArrowUp.svg'
import { ReactComponent as DiagonalArrowDown } from 'src/assets/svg/diagonalArrowDown.svg'
import styles from './campaignComparisonChart.module.scss'

interface CampaignData {
  id: string
  mentions: number
  name: string
  sentiments?: {
    negative: number
    neutral: number
    positive: number
  }
  sources?: Record<string, number>
}

interface MentionsTabProps {
  benchmarkCampaign: string | null
  selectedData: CampaignData[]
  stats?: any
}

export const MentionsTab: FC<MentionsTabProps> = ({
  selectedData,
  benchmarkCampaign,
  stats,
}) => {
  // Limit to max 9 campaigns
  const limitedData = selectedData.slice(0, 9)

  // Use API stats if available, otherwise calculate from data
  const totalMentions =
    stats?.totalMentions ??
    limitedData.reduce((sum, campaign) => sum + campaign.mentions, 0)

  let mostMentions = stats?.mostMentionsCampaign
  let leastMentions = stats?.leastMentionsCampaign

  // If total mentions is 0, don't show any campaign names
  if (totalMentions === 0) {
    mostMentions = null
    leastMentions = null
  } else {
    // Calculate from data if not provided by API
    if (!mostMentions || !leastMentions) {
      const campaignsWithMentions = limitedData.filter((c) => c.mentions > 0)

      if (campaignsWithMentions.length > 0) {
        if (!mostMentions) {
          mostMentions = campaignsWithMentions.reduce((max, campaign) =>
            campaign.mentions > max.mentions ? campaign : max
          )
        }
        if (!leastMentions) {
          leastMentions = campaignsWithMentions.reduce((min, campaign) =>
            campaign.mentions < min.mentions ? campaign : min
          )
        }
      }
    }
  }

  // Calculate max value for chart scaling
  const maxMentions = Math.max(...limitedData.map((c) => c.mentions))
  // Ensure minimum scale for better visibility
  const chartMax = Math.max(Math.ceil(maxMentions * 1.2), 10) // Minimum of 10, or 20% above max

  // Generate Y-axis labels - always show 6 labels (0 to max)
  const yAxisLabels = []
  const step = chartMax / 5
  for (let i = 5; i >= 0; i--) {
    const value = Math.round(step * i)
    yAxisLabels.push(value)
  }

  return (
    <>
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <AnnouncementIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>
              {/* Only the compared campaigns, unlike the page's own Total
                  Mentions above — say so, or the two read as a contradiction. */}
              Mentions · {limitedData.length} selected
              {limitedData.length === 1 ? ' campaign' : ' campaigns'}
            </div>
            <div className={styles.statValue}>
              {totalMentions.toLocaleString()}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Most Mentions</div>
            <div className={styles.statValue}>
              {mostMentions?.name || 'No data'}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Least Mentions</div>
            <div className={styles.statValue}>
              {leastMentions?.name || 'No data'}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.chart}>
          <div className={styles.yAxis}>
            {yAxisLabels.slice(0, 5).map((label) => (
              <div className={styles.yLabel} key={label}>
                {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
              </div>
            ))}
          </div>

          <div className={styles.chartArea}>
            <div className={styles.gridLines}>
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
            </div>

            <div className={styles.bars}>
              {limitedData.map((campaign) => {
                // Calculate height in pixels (260px is max height)
                const maxBarHeight = 260
                const heightPixels =
                  chartMax > 0
                    ? (campaign.mentions / chartMax) * maxBarHeight
                    : 0
                // For non-zero values, ensure minimum 20px height for visibility
                // For zero values, set height to 0
                const height =
                  campaign.mentions > 0 ? Math.max(heightPixels, 20) : 0
                const isBenchmark = campaign.id === benchmarkCampaign

                // Calculate percentage difference from benchmark
                let percentageDiff = null
                if (benchmarkCampaign && !isBenchmark) {
                  const benchmarkData = limitedData.find(
                    (c) => c.id === benchmarkCampaign
                  )
                  if (benchmarkData && benchmarkData.mentions > 0) {
                    percentageDiff =
                      ((campaign.mentions - benchmarkData.mentions) /
                        benchmarkData.mentions) *
                      100
                  }
                }

                return (
                  <div className={styles.barWrapper} key={campaign.id}>
                    <div
                      className={`${styles.bar} ${
                        isBenchmark ? styles.benchmark : ''
                      }`}
                      style={{ height: `${height}px` }}
                    />
                    <div className={styles.barTooltip}>
                      <div className={styles.tooltipLabel}>Mentions</div>
                      <div className={styles.tooltipContent}>
                        <span className={styles.tooltipValue}>
                          {campaign.mentions.toLocaleString()}
                        </span>
                        {percentageDiff !== null && (
                          <span
                            className={`${styles.tooltipDiff} ${
                              percentageDiff >= 0
                                ? styles.positive
                                : styles.negative
                            }`}
                          >
                            {percentageDiff >= 0 ? (
                              <DiagonalArrowUp
                                className={styles.tooltipArrow}
                              />
                            ) : (
                              <DiagonalArrowDown
                                className={styles.tooltipArrow}
                              />
                            )}
                            {Math.abs(percentageDiff).toFixed(1)}%
                          </span>
                        )}
                      </div>
                      {isBenchmark && (
                        <div className={styles.tooltipBenchmark}>Benchmark</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className={styles.xAxis}>
              {limitedData.map((campaign) => {
                const isBenchmark = campaign.id === benchmarkCampaign
                return (
                  <div
                    className={styles.xLabel}
                    key={campaign.id}
                    onMouseEnter={(e) => {
                      const nameElement = e.currentTarget.querySelector(
                        `.${styles.campaignName}`
                      ) as HTMLElement
                      const tooltipElement = e.currentTarget.querySelector(
                        `.${styles.campaignNameTooltip}`
                      ) as HTMLElement
                      if (
                        nameElement &&
                        tooltipElement &&
                        nameElement.scrollWidth > nameElement.clientWidth
                      ) {
                        tooltipElement.style.opacity = '1'
                        tooltipElement.style.visibility = 'visible'
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltipElement = e.currentTarget.querySelector(
                        `.${styles.campaignNameTooltip}`
                      ) as HTMLElement
                      if (tooltipElement) {
                        tooltipElement.style.opacity = '0'
                        tooltipElement.style.visibility = 'hidden'
                      }
                    }}
                  >
                    <span className={styles.campaignName}>
                      {campaign.name}
                      {isBenchmark && (
                        <StarIcon className={styles.benchmarkIcon} />
                      )}
                    </span>
                    <div className={styles.campaignNameTooltip}>
                      {campaign.name}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className={styles.yAxisRight}>
            {yAxisLabels.slice(0, 5).map((label) => (
              <div className={styles.yLabel} key={label}>
                {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}
